import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { useBeta } from '../../contexts/BetaContext';
import { getStorageItem } from '../../utils/localStorage';
import { OSCAR_INVITE_2026 } from '../../config/constants';
import './SimpleHomePage.css';

const InfoIcon = () => (
  <svg
    className="simple-home__deadline-icon"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const InstagramIcon = () => (
  <svg
    className="simple-home__instagram-icon"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const SimpleHomePage = () => {
  const navigate = useNavigate();
  const { settings, loading: settingsLoading } = useSettings();
  const { activePartyId } = useBeta();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [hasExistingBallot, setHasExistingBallot] = useState(false);
  const hasPartyEnded = Boolean(settings?.dateEnded);

  useEffect(() => {
    if (!activePartyId) return;
    const userId = getStorageItem('userId', activePartyId);
    const ballotId = getStorageItem('ballotId', activePartyId);
    setHasExistingBallot(!!(userId && ballotId));
  }, [activePartyId]);

  useEffect(() => {
    if (hasPartyEnded) {
      setTimeRemaining(null);
      return;
    }

    if (!settings?.partyStartDate) return;
    const targetDate = new Date(settings.partyStartDate);
    if (isNaN(targetDate.getTime())) return;

    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;
      if (diff <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0, ended: true });
        return;
      }
      setTimeRemaining({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
        ended: false
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [hasPartyEnded, settings?.partyStartDate]);

  const formatDateShort = () => {
    if (!settings?.partyStartDate) return 'Sunday, March 15, 2026';
    const date = new Date(settings.partyStartDate);
    if (isNaN(date.getTime())) return 'Sunday, March 15, 2026';
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/Los_Angeles'
    });
  };

  const formatTimeShort = () => {
    if (!settings?.partyStartDate) return '4:00 PM PST';
    const date = new Date(settings.partyStartDate);
    if (isNaN(date.getTime())) return '4:00 PM PST';
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Los_Angeles',
      timeZoneName: 'short'
    });
  };

  if (settingsLoading) {
    return (
      <div className="simple-home">
        <div className="simple-home__container simple-home__loading">
          <div className="simple-home__spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const { social, ballotDeadlineNote } = OSCAR_INVITE_2026;
  return (
    <div className="simple-home">
      <div className="simple-home__container">
        <header className="simple-home__header">
          <div className="simple-home__title-wrap">
            <span className="simple-home__title-official">The Official</span>
            <span className="simple-home__title-main">{OSCAR_INVITE_2026.partyTitle.replace('The Official ', '').replace(' Party', '')}</span>
            <span className="simple-home__title-party">party</span>
            <div className="simple-home__academy-awards">
              <div className="simple-home__academy-line" />
              <span className="simple-home__academy-text">For the {OSCAR_INVITE_2026.academyAwards}</span>
              <div className="simple-home__academy-line" />
            </div>
          </div>
        </header>

        <p className="simple-home__tagline">{OSCAR_INVITE_2026.tagline}</p>

        <section className="simple-home__card">
          <div className="simple-home__card-inner">
            <p className="simple-home__card-label">
              {hasPartyEnded ? 'The party has ended' : timeRemaining?.ended ? 'The party has started!' : 'Countdown to Showtime'}
            </p>
            {hasPartyEnded ? (
              <div className="simple-home__timer simple-home__timer--results">
                <div className="simple-home__timer-segment simple-home__timer-segment--results">
                  <span className="simple-home__timer-value">{settings?.year || new Date().getFullYear()} Results</span>
                  <span className="simple-home__timer-unit simple-home__timer-unit--results">Now Live</span>
                </div>
              </div>
            ) : timeRemaining && !timeRemaining.ended && (
              <div className="simple-home__timer">
                {timeRemaining.days > 0 && (
                  <div className="simple-home__timer-segment">
                    <span className="simple-home__timer-value">
                      {String(timeRemaining.days).padStart(2, '0')}
                    </span>
                    <span className="simple-home__timer-unit">Days</span>
                  </div>
                )}
                <div className="simple-home__timer-segment">
                  <span className="simple-home__timer-value">
                    {String(timeRemaining.hours).padStart(2, '0')}
                  </span>
                  <span className="simple-home__timer-unit">Hrs</span>
                </div>
                <div className="simple-home__timer-segment">
                  <span className="simple-home__timer-value">
                    {String(timeRemaining.minutes).padStart(2, '0')}
                  </span>
                  <span className="simple-home__timer-unit">Mins</span>
                </div>
                <div className="simple-home__timer-segment">
                  <span className="simple-home__timer-value">
                    {String(timeRemaining.seconds).padStart(2, '0')}
                  </span>
                  <span className="simple-home__timer-unit">Secs</span>
                </div>
              </div>
            )}
          </div>
        </section>

        <div className="simple-home__cta">
          {!hasPartyEnded && (
            <div className="simple-home__date-block">
              <p className="simple-home__date">{formatDateShort()}</p>
              <div className="simple-home__time">
                <span>{formatTimeShort()}</span>
                <span className="simple-home__time-dot" />
                <span className="simple-home__card-text--muted" style={{ margin: 0 }}>
                  Pregame @ {OSCAR_INVITE_2026.pregameTime}
                </span>
              </div>
            </div>
          )}

          <div className="simple-home__btn-wrap">
            {hasPartyEnded ? (
              <div className="simple-home__btn-group">
                <button
                  type="button"
                  className="simple-home__btn simple-home__btn--primary"
                  onClick={() => navigate('/results')}
                >
                  View Results
                </button>
                <button
                  type="button"
                  className="simple-home__btn simple-home__btn--secondary"
                  onClick={() => navigate('/submit')}
                >
                  View Your Ballot
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="simple-home__btn simple-home__btn--primary"
                onClick={() => navigate('/submit')}
              >
                {hasExistingBallot ? 'View Your Ballot' : 'Create Your Ballot'}
              </button>
            )}
          </div>

          <footer className="simple-home__footer">
            {!hasPartyEnded && (
              <div className="simple-home__deadline">
                <InfoIcon />
                <p className="simple-home__deadline-text">
                  <strong>Ballot Deadline</strong>
                  {ballotDeadlineNote}
                </p>
              </div>
            )}

            <div className="simple-home__social">
              <a
                href="https://www.instagram.com/mroscarparty/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="simple-home__social-label">Follow for updates</span>
                <span className="simple-home__social-handle">
                  <InstagramIcon />
                  {social.instagram}
                </span>
              </a>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default SimpleHomePage;
